import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Plus, UserRound } from 'lucide-react';
import { ActionStack, Screen } from '@/app/layout/Screen';
import { Button } from '@/components/ui/Button';
import { LoadFailure, PanelSkeleton, StateView } from '@/components/ui/Feedback';
import { Input, OptionCard } from '@/components/ui/Form';
import { Sheet } from '@/components/ui/Sheet';
import { Badge, Helper, Lead, MiniIcon, PageTitle, Panel, Row } from '@/components/ui/Surface';
import { canCreateProfile, useDoctorProfilesStore } from '@/store/doctorProfilesStore';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import { getBridge } from '@/platform';

/**
 * Экран 23 на реальном бэкенде. Профессиональные профили.
 *
 * Профилей не больше трёх, и у каждого своя анкета и свой курс. Смена профиля
 * подтверждается: она меняет содержимое всего приложения, а не только подпись
 * на экране, и случайное переключение выглядело бы как потеря данных.
 *
 * Состояния: загрузка, ошибка с повтором, пусто, список, создание.
 */
export function BackendProfilesScreen() {
  const navigate = useNavigate();
  const toast = useUiStore((state) => state.toast);

  const profiles = useDoctorProfilesStore((state) => state.profiles);
  const activeId = useDoctorProfilesStore((state) => state.activeId);
  const loading = useDoctorProfilesStore((state) => state.loading);
  const busy = useDoctorProfilesStore((state) => state.busy);
  const error = useDoctorProfilesStore((state) => state.error);
  const canCreate = useDoctorProfilesStore((state) => canCreateProfile(state.profiles));
  const load = useDoctorProfilesStore((state) => state.load);
  const select = useDoctorProfilesStore((state) => state.select);
  const create = useDoctorProfilesStore((state) => state.create);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [copyFrom, setCopyFrom] = useState<number | null>(null);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && profiles.length === 0) {
    return (
      <Screen subtitle="Профессиональные профили" back backTo="/profile" tab="profile">
        <PanelSkeleton count={3} />
      </Screen>
    );
  }

  if (error && profiles.length === 0) {
    return (
      <Screen subtitle="Профессиональные профили" back backTo="/profile" tab="profile">
        <LoadFailure title="Профили не загрузились" description={error} onRetry={() => void load()} />
      </Screen>
    );
  }

  const switchTo = async (doctorProfileId: number, profileName: string) => {
    const confirmed = await getBridge().showConfirm(
      `Переключиться на профиль «${profileName}»? Анкета и курс откроются для него.`
    );
    if (!confirmed) return;

    const ok = await select(doctorProfileId);
    if (!ok) {
      toast('Не удалось переключить профиль', 'error');
      return;
    }

    toast(`Активен профиль «${profileName}»`, 'success');
    // Если профиль требует анкеты, сессия уже переведена в регистрацию и гейт
    // покажет нужный шаг: уводить на курс в этом случае некуда
    if (useAuthStore.getState().phase === 'authenticated') navigate('/course');
  };

  const submitCreate = async () => {
    const profile = await create(name, copyFrom ?? undefined);
    if (!profile) {
      toast('Не удалось создать профиль', 'error');
      return;
    }
    setCreating(false);
    setName('');
    setCopyFrom(null);
    toast('Профиль создан', 'success');
    // Новый профиль пуст, поэтому бэкенд возвращает сессию в регистрацию и
    // просит заполнить анкету. Гейт покажет нужный шаг сам
    if (useAuthStore.getState().phase === 'authenticated') navigate('/course');
  };

  return (
    <Screen subtitle="Профессиональные профили" back backTo="/profile" tab="profile">
      <PageTitle>Профессиональные профили</PageTitle>
      <Lead>
        У каждого профиля своя анкета, курс и материалы. Данные между профилями не смешиваются.
      </Lead>

      {profiles.length === 0 ? (
        <StateView
          icon={<UserRound className="h-6 w-6" aria-hidden />}
          title="Профилей пока нет"
          description="Создайте профиль, чтобы начать курс."
          primary={
            <Button variant="primary" block onClick={() => setCreating(true)}>
              Создать профиль
            </Button>
          }
        />
      ) : (
        <>
          {profiles.map((profile) => {
            const active = profile.doctor_profile_id === activeId;
            return (
              <Panel key={profile.doctor_profile_id} className="mb-2.5">
                <Row
                  icon={
                    <MiniIcon tone={active ? 'brand' : 'lime'}>
                      {active ? (
                        <Check className="h-[17px] w-[17px]" aria-hidden />
                      ) : (
                        <UserRound className="h-[17px] w-[17px]" aria-hidden />
                      )}
                    </MiniIcon>
                  }
                  title={profile.profile_name}
                  subtitle={active ? 'Активный профиль' : 'Нажмите, чтобы переключиться'}
                  trailing={active ? <Badge tone="success">Активен</Badge> : undefined}
                  chevron={!active}
                  onClick={
                    active
                      ? undefined
                      : () => void switchTo(profile.doctor_profile_id, profile.profile_name)
                  }
                />
              </Panel>
            );
          })}

          <ActionStack>
            <Button
              block
              disabled={!canCreate}
              icon={<Plus className="h-4 w-4" aria-hidden />}
              onClick={() => setCreating(true)}
            >
              Добавить профиль
            </Button>
            {!canCreate ? <Helper>Больше трёх профилей создать нельзя</Helper> : null}
          </ActionStack>
        </>
      )}

      <Sheet open={creating} onClose={() => setCreating(false)} title="Новый профиль">
        <Input
          label="Название профиля"
          placeholder="Например: Функциональная медицина"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        {profiles.length > 0 ? (
          <>
            <p className="mb-1.5 mt-4 text-[12px] font-medium">Скопировать анкету</p>
            <OptionCard
              title="Заполнить заново"
              selected={copyFrom === null}
              onSelect={() => setCopyFrom(null)}
            />
            {profiles.map((profile) => (
              <OptionCard
                key={profile.doctor_profile_id}
                title={`Из профиля «${profile.profile_name}»`}
                subtitle="Ответы можно будет изменить"
                selected={copyFrom === profile.doctor_profile_id}
                onSelect={() => setCopyFrom(profile.doctor_profile_id)}
              />
            ))}
          </>
        ) : null}

        <Button
          variant="primary"
          block
          className="mt-4"
          loading={busy}
          disabled={!name.trim()}
          onClick={() => void submitCreate()}
        >
          Создать профиль
        </Button>
      </Sheet>
    </Screen>
  );
}
